/**
 * Player — the Space Newt.
 *
 * The visible ship is drawn with a Phaser.Graphics rectangle so it's
 * guaranteed to render on every device regardless of texture-loading
 * state. Game logic still uses the Sprite body for movement, hit-tests
 * and overlap callbacks.
 *
 * Three attack modes:
 *   - Basic laser (auto-fire while moving; pool-managed in GameScene).
 *     Every upgrade (multishot / spread / fire-rate / bullet-size) is
 *     consumed here.
 *   - Chomp (close-range melee, short cooldown).
 *   - Charging godzilla beam (hold to charge, release for a horizontal
 *     beam that pierces everything in its path).
 */
import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../Game";
import { TEX } from "../textures";
import type { Projectile } from "./Projectile";
import { ACTIVE_DIFFICULTY } from "../config/difficultyConfig";

const PLAYER_SPEED = 260; // px/sec
/** Visible player ship dimensions on canvas — a white rectangle. */
const SHIP_W = 72;
const SHIP_H = 43;

export class Player extends Phaser.Physics.Arcade.Sprite {
  public lives = ACTIVE_DIFFICULTY.startingLives;
  public hp = ACTIVE_DIFFICULTY.startingHp;
  public maxHp = ACTIVE_DIFFICULTY.startingHp;
  public invincibleUntil = 0;

  private blinkEvent: Phaser.Time.TimerEvent | null = null;
  private lastChompAt = -Infinity;
  private facing: "left" | "right" = "right";

  // Beam charging state
  public charging = false;
  public chargeStartAt = 0;
  public chargeLevel = 0; // 0..1
  public beam: Projectile | null = null;

  // Tunables
  private get chompCooldownMs() { return ACTIVE_DIFFICULTY.chompCooldownMs; }
  /** Chomp reach must reach at least one enemy without the newt
   *  physically touching it, otherwise the only way to chomp is to run
   *  into the enemy and the player gets the bounce damage on every bite. */
  private readonly chompRange = 120; // 120 radius is ~240 diameter, easily twice the player size (72x43)
  private get beamChargeMs() { return ACTIVE_DIFFICULTY.beamChargeMs; } // full charge time
  private get invulnMs() { return ACTIVE_DIFFICULTY.invulnerabilityMs; }

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, TEX.PLAYER);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    const texW = 300;
    const texH = 190;
    const scale = Math.min(SHIP_W / texW, SHIP_H / texH);
    this.setScale(scale);
    this.setOrigin(0.5, 0.5);
    const baseW = SHIP_W / scale;
    const baseH = SHIP_H / scale;
    
    // Hitbox is 10% smaller overall, and 25% smaller from the left (tail)
    const hitW = baseW * 0.65; // 100% - 25% left - 10% right
    const hitH = baseH * 0.80; // 100% - 10% top - 10% bottom
    
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(hitW, hitH);
    body.setOffset(baseW * 0.25, baseH * 0.10);
    this.setCollideWorldBounds(true);
    // Depth ordering: player sprite above backgrounds (depth 0–1),
    // below HUD (depth 20+).
    this.setDepth(14);
  }



  destroy(fromScene?: boolean): void {
    super.destroy(fromScene);
  }

  /** Called every frame from GameScene to follow the physics sprite. */
  public refreshVisual(): void {
    // The sprite renders itself.
  }

  public moveWith(velocity: Phaser.Math.Vector2, speedMultiplier = 1.0): void {
    const currentSpeed = PLAYER_SPEED * speedMultiplier;
    this.setVelocity(velocity.x * currentSpeed, velocity.y * currentSpeed);
    this.facing = "right";

    // Clamp to the bottom strip so the player can't fly above the horizon.
    const minY = GAME_HEIGHT * 0.18;
    const maxY = GAME_HEIGHT - 40;
    if (this.y < minY) this.y = minY;
    if (this.y > maxY) this.y = maxY;
    if (this.x < 20) this.x = 20;
    if (this.x > GAME_WIDTH - 20) this.x = GAME_WIDTH - 20;
  }

  /** True if the chomp cooldown has elapsed since the last chomp. */
  public canChomp(now: number): boolean {
    return now - this.lastChompAt >= this.chompCooldownMs;
  }

  public getChompCooldownProgress(now: number): number {
    const elapsed = now - this.lastChompAt;
    if (elapsed >= this.chompCooldownMs) return 1.0;
    return elapsed / this.chompCooldownMs;
  }

  public markChompUsed(now: number): void {
    this.lastChompAt = now;
  }

  public chompRangePx(): number {
    return this.chompRange;
  }

  public chompOrigin(): { x: number; y: number; facing: "left" | "right" } {
    return {
      x: this.x,
      y: this.y,
      facing: this.facing,
    };
  }

  public startCharging(now: number): void {
    if (this.charging) return;
    this.charging = true;
    this.chargeStartAt = now;
    this.chargeLevel = 0;
  }

  public updateCharge(now: number): number {
    if (!this.charging) return 0;
    this.chargeLevel = Math.min(1, (now - this.chargeStartAt) / this.beamChargeMs);
    return this.chargeLevel;
  }

  public cancelCharge(): void {
    this.charging = false;
    this.chargeLevel = 0;
  }

  public takeHit(now: number): { gameover: boolean; lifeLost: boolean } {
    if (now < this.invincibleUntil) return { gameover: false, lifeLost: false };
    this.hp -= 1;
    let lifeLost = false;
    if (this.hp <= 0) {
      this.lives -= 1;
      lifeLost = true;
      this.hp = this.maxHp;
    }
    this.invincibleUntil = now + this.invulnMs;
    this.startBlink(now + this.invulnMs);
    if (this.lives <= 0) {
      this.disableBody(true, true);
      return { gameover: true, lifeLost };
    }
    if (lifeLost) {
      this.setPosition(80, GAME_HEIGHT - 80);
    }
    return { gameover: false, lifeLost };
  }

  private startBlink(until: number): void {
    this.blinkEvent?.remove();
    this.blinkEvent = this.scene.time.addEvent({
      delay: 80,
      loop: true,
      callback: () => {
        // Toggle the Sprite visibility so it blinks
        const v = !this.visible;
        this.setVisible(v);
        if (this.scene.time.now >= until) {
          this.setVisible(true);
          this.blinkEvent?.remove();
          this.blinkEvent = null;
        }
      },
    });
  }
}